import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { getCategoryColor } from '../utils/constants';
import supabase from '../lib/supabase';

const { FiArrowLeft, FiSend, FiLoader, FiAlertTriangle, FiPlus } = FiIcons;

const LearningDetails = ({ learningId, onNavigate }) => {
  const [newComment, setNewComment] = useState('');
  const [learning, setLearning] = useState(null);
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (learningId) {
      fetchLearningDetails();
    }
  }, [learningId]);

  const fetchLearningDetails = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch learning details
      const { data: learningData, error: learningError } = await supabase
        .from('learnings_kb2024')
        .select('*')
        .eq('id', learningId)
        .single();

      if (learningError) throw learningError;
      setLearning(learningData);

      // In a real app, you'd fetch comments from a comments table
      // For now, we'll start with an empty array
      setComments([]);

    } catch (error) {
      console.error('Error fetching learning details:', error);
      setError('Unable to load learning details. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitComment = async (e) => {
    e.preventDefault();
    if (!newComment.trim()) return;

    try {
      setSubmitting(true);
      // In a real app, you'd save this to a comments table
      const newCommentObj = {
        id: Date.now(),
        commentText: newComment,
        commentedBy: 'Current User',
        createdAt: new Date().toISOString()
      };

      setComments([...comments, newCommentObj]);
      setNewComment('');
      console.log('New comment submitted:', newComment);
    } catch (error) {
      console.error('Error submitting comment:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="flex items-center gap-3">
            <SafeIcon icon={FiLoader} className="w-8 h-8 text-green-600 animate-spin" />
            <span className="text-lg text-gray-600">Loading learning...</span>
          </div>
        </div>
      </div>
    );
  }

  if (error || !learning) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-8 text-center">
          <div className="text-red-600 mb-4">
            <SafeIcon icon={FiAlertTriangle} className="w-12 h-12 mx-auto" />
          </div>
          <h3 className="text-lg font-medium text-red-900 mb-2">Error Loading Learning</h3>
          <p className="text-red-700 mb-4">{error || 'Learning not found'}</p>
          <button
            onClick={() => onNavigate('learnings')}
            className="bg-red-600 hover:bg-red-700 text-white font-medium py-2 px-4 rounded-lg transition-colors duration-200"
          >
            Back to Learnings
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-6">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-8"
      >
        <button
          onClick={() => onNavigate('learnings')}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4 transition-colors duration-200"
        >
          <SafeIcon icon={FiArrowLeft} className="w-4 h-4" />
          Back to Learnings
        </button>
        
        {/* Create New Learning Button */}
        <div className="mb-4">
          <button
            onClick={() => onNavigate('learning-form')}
            className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white font-medium py-2 px-4 rounded-lg transition-colors duration-200"
          >
            <SafeIcon icon={FiPlus} className="w-4 h-4" />
            Share a Learning
          </button>
        </div>
      </motion.div>

      {/* Learning Details */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="bg-white rounded-xl shadow-sm border border-gray-100 p-8 mb-8"
      >
        <div className="flex justify-between items-start mb-4">
          <h1 className="text-2xl font-bold text-gray-900">{learning.title}</h1>
          <span className={`px-3 py-1 text-sm font-medium rounded-full ${getCategoryColor(learning.category)}`}>
            {learning.category}
          </span>
        </div>

        <div className="prose max-w-none mb-6">
          <div className="text-gray-700 leading-relaxed whitespace-pre-line">
            {learning.content}
          </div>
        </div>

        {learning.resources && (
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Additional Resources</h3>
            <div className="bg-blue-50 border-l-4 border-blue-400 p-4">
              <div className="text-blue-700 leading-relaxed whitespace-pre-line">
                {learning.resources}
              </div>
            </div>
          </div>
        )}

        <div className="text-sm text-gray-600">
          Shared by <span className="font-medium">{learning.shared_by}</span> on {formatDate(learning.created_at)}
        </div>
      </motion.div>

      {/* Comments Section */}
      {comments.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white rounded-xl shadow-sm border border-gray-100 mb-8"
        >
          <div className="p-6 border-b border-gray-100">
            <h2 className="text-xl font-semibold text-gray-900">
              Comments ({comments.length})
            </h2>
          </div>
          <div className="divide-y divide-gray-100">
            {comments.map((comment, index) => (
              <motion.div
                key={comment.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 + index * 0.1 }}
                className="p-6"
              >
                <p className="text-gray-700 mb-4 leading-relaxed whitespace-pre-line">{comment.commentText}</p>
                <div className="text-sm text-gray-600">
                  By <span className="font-medium">{comment.commentedBy}</span> on {formatDate(comment.createdAt)}
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>
      )}

      {/* Comment Form */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
        className="bg-white rounded-xl shadow-sm border border-gray-100 p-8"
      >
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Add a Comment</h3>
        <form onSubmit={handleSubmitComment}>
          <div className="mb-4">
            <textarea
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              placeholder="Share your thoughts or ask questions about this learning..."
              rows={4}
              disabled={submitting}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all duration-200 resize-none disabled:bg-gray-100"
              required
            />
          </div>
          <button
            type="submit"
            disabled={submitting || !newComment.trim()}
            className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white font-medium py-3 px-6 rounded-lg transition-colors duration-200 disabled:opacity-50"
          >
            {submitting ? (
              <>
                <SafeIcon icon={FiLoader} className="w-4 h-4 animate-spin" />
                Submitting...
              </>
            ) : (
              <>
                <SafeIcon icon={FiSend} className="w-4 h-4" />
                Submit Comment
              </>
            )}
          </button>
        </form>
      </motion.div>
    </div>
  );
};

export default LearningDetails;